import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { Info } from 'lucide-react'

import { EmptyState } from '@/components/EmptyState/EmptyState'
import { Input } from '@/components/ui/input'
import { useLazyGetAccountHistoryQuery } from '@/store/api/Account/Account.query'
import { formatDate } from '@/utils/date'
import { SocialAnalysisItem } from './SocialAnalysisItem'

interface ReportsTabProps {
  isActive: boolean
}

export const ReportsTab = ({ isActive }: ReportsTabProps) => {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [allReports, setAllReports] = useState<any[]>([])
  const pageSize = 10

  const [
    fetchHistory,
    { data, isLoading: isLoadingFirstTime, isFetching, isError },
  ] = useLazyGetAccountHistoryQuery()

  const makeRequest = useCallback(
    (page: number) => {
      fetchHistory({ page, pageSize })
    },
    [fetchHistory, pageSize],
  )

  useEffect(() => {
    if (isActive) {
      makeRequest(currentPage)
    }
  }, [makeRequest, currentPage, isActive])

  useEffect(() => {
    if (data?.info.list) {
      setAllReports((prevReports) => {
        if (currentPage === 1) {
          return data.info.list
        }
        return [...prevReports, ...data.info.list]
      })
    }
  }, [data, currentPage])

  const hasMoreData = useMemo(() => {
    if (!data?.info) return false
    return data.info.page < data.info.pages
  }, [data])

  const fetchMoreOnPagination = () => {
    if (!isFetching && hasMoreData) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: hasMoreData,
    onLoadMore: fetchMoreOnPagination,
    disabled: isError,
    rootMargin: '0px 0px 400px 0px',
  })

  const isLoadingFirstPage =
    (isFetching && currentPage === 1) || isLoadingFirstTime

  const filteredReports = useMemo(() => {
    if (!search) return allReports

    const searchLower = search.toLowerCase()
    const filtered = allReports.filter((report: any) => {
      const name = report.data.inputs.name?.toLowerCase() || ''
      const lastName = report.data.inputs.lastName?.toLowerCase() || ''
      const email = report.data.inputs.email?.toLowerCase() || ''
      const date = formatDate(report.data.createdAt, 'MM/dd/yyyy') || ''

      return (
        name.includes(searchLower) ||
        lastName.includes(searchLower) ||
        email.includes(searchLower) ||
        date.includes(searchLower)
      )
    })

    return filtered
  }, [allReports, search])

  if (allReports.length === 0 && !isLoadingFirstPage) {
    return (
      <div className="flex flex-col align-middle items-center justify-center h-full bg-baseColor rounded-2xl p-10">
        <EmptyState
          text={
            search === ''
              ? t('dashboard.reports.emptyState')
              : `${t('dashboard.reports.noResultsFor')} "${search}"`
          }
        />
      </div>
    )
  }

  return (
    <div className={'flex flex-col gap-5 h-full'}>
      <div className="w-full flex justify-between sm:items-center max-sm:flex-col max-sm:gap-5">
        <Input
          placeholder={t('dashboard.reports.search')}
          className="flex h-[46px] w-[354.67px] rounded-lg max-sm:w-full"
          value={search}
          useDebounce={true}
          debounceTime={300}
          onChange={handleSearch}
        />
      </div>

      <div
        ref={rootRef}
        className="flex flex-col gap-4 custom-scrollbar overflow-y-auto h-full"
      >
        {filteredReports.map((report, index) => (
          <SocialAnalysisItem {...report.data} key={index} />
        ))}
        {filteredReports.length === 0 && search !== '' && (
          <div className="flex justify-center h-full items-center">
            <p className="text-xl font-medium text-neutralColor80">
              {t('dashboard.reports.noResultsFor')} "{search}"
            </p>
          </div>
        )}

        {hasMoreData && (
          <div ref={sentryRef} className="py-4 text-center">
            {isFetching && (
              <p className="text-xl font-medium text-neutralColor80">
                {t('common.loading')}...
              </p>
            )}
          </div>
        )}
      </div>
      <div className="flex items-start justify-center gap-1">
        <Info size={16} className="text-neutralColor60" />
        <p className="text-xs font-normal text-neutralColor60 text-center ">
          {t('dashboard.reports.disclaimer')}
        </p>
      </div>
    </div>
  )
}
